import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  LifeBuoy,
  ScrollText,
  Search,
  ShieldAlert,
  SlashSquare,
  ToggleLeft,
} from "lucide-react";

import { DEFAULT_BRANDING, type Branding } from "@lagoon/config/branding";
import {
  AppLayout,
  Button,
  LagoonLockup,
  Mono,
  type NavSection,
  SplashLayout,
  Text,
} from "@lagoon/ui";

import { api, type ApiError, type Health, type Me } from "./api.js";
import { RoleBadge } from "./components/badges.js";
import { parseHash, type View } from "./router.js";
import { AuditScreen } from "./screens/audit.js";
import { DenylistScreen } from "./screens/denylist.js";
import { FlagsScreen } from "./screens/flags.js";
import { OrgScreen, OrgsScreen } from "./screens/orgs.js";
import { OwnerScreen } from "./screens/owner.js";
import { ScreeningScreen } from "./screens/screening.js";
import { SearchScreen } from "./screens/search.js";
import { SiteScreen } from "./screens/site.js";

/**
 * The console shell.
 *
 * Nine views, no router library: the surface is small and the URL only ever
 * needs to carry which view and which record, so a hash is enough and it keeps
 * the dependency out of an image that operators run against production. The
 * hash is parsed in `router.ts`, which is a pure function with a test, because
 * the one thing this shell has actually got wrong was the parse (LGN-125).
 *
 * The chrome is `AppLayout` from the design library (LGN-106): a rail on wide
 * screens, a drawer on narrow ones, a skip link, real `nav` and `main`
 * landmarks and `aria-current` on the item you are looking at. The console
 * used to hand-roll a flex header with a row of underlined links, which had
 * none of that.
 */

/** Which navigation item is lit for a view. A site lives under the search. */
function navIdFor(view: View): string {
  if (view.name === "site" || view.name === "owner") return "search";
  if (view.name === "org") return "orgs";
  return view.name;
}

export function App(): React.JSX.Element {
  const [view, setView] = useState<View>(() => parseHash(window.location.hash));
  const branding = readBranding();

  useEffect(() => {
    const onChange = () => setView(parseHash(window.location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);

  const me = useQuery<Me, ApiError>({
    queryKey: ["me"],
    queryFn: () => api.me(),
    retry: false,
  });

  /**
   * The counts, fetched once for the whole console.
   *
   * The screening queue depth is in the rail as well as on the overview: a
   * flagged release is published and being served while it waits, so an
   * operator needs to know something is waiting without going to look.
   */
  const health = useQuery({
    queryKey: ["health"],
    queryFn: () => api.health(),
    refetchInterval: 60_000,
    enabled: me.data !== undefined,
  });

  const sections = useNavSections(health.data, me.data?.can.administer ?? false);

  if (me.isPending) {
    return (
      <div className="lg:grid lg:min-h-dvh lg:place-items-center lg:bg-background">
        <Text size="sm" tone="muted">
          Checking your session…
        </Text>
      </div>
    );
  }

  if (me.error) {
    // 401 and 403 mean different things to an operator: one is "sign in", the
    // other is "you signed in and this account is not allowed here".
    return (
      <SignIn forbidden={me.error.isForbidden} message={me.error.message} branding={branding} />
    );
  }

  return (
    <AppLayout
      sections={sections}
      activeId={navIdFor(view)}
      brand={<LagoonLockup product={branding.productName} />}
      headerActions={<Identity me={me.data} />}
    >
      {view.name === "search" && <SearchScreen health={health.data} />}
      {view.name === "site" && <SiteScreen siteId={view.id} me={me.data} />}
      {view.name === "owner" && <OwnerScreen ownerId={view.id} me={me.data} />}
      {view.name === "orgs" && <OrgsScreen me={me.data} />}
      {view.name === "org" && <OrgScreen orgId={view.id} me={me.data} />}
      {/*
        Keyed on the filters, so following the audit link from a second site
        re-filters instead of showing the first site's rows: the screen holds
        paging state, and remounting is the honest way to reset it.
      */}
      {view.name === "audit" && (
        <AuditScreen
          key={`${view.siteId}|${view.actor}`}
          siteId={view.siteId}
          initialActor={view.actor}
        />
      )}
      {view.name === "denylist" && <DenylistScreen me={me.data} />}
      {view.name === "screening" && <ScreeningScreen me={me.data} />}
      {view.name === "flags" && <FlagsScreen me={me.data} />}
    </AppLayout>
  );
}

/**
 * The rail.
 *
 * Memoised because `AppLayout` takes the sections as a prop and rebuilding the
 * array on every render would remount nothing but would churn the tree for no
 * reason. The only value in it that moves is the screening badge.
 */
function useNavSections(health: Health | undefined, canAdminister: boolean): NavSection[] {
  const queue = health?.screeningQueue ?? 0;

  return useMemo(
    () => [
      {
        id: "tenants",
        label: "Tenants",
        items: [
          { id: "search", label: "Sites and owners", href: "#/", icon: <Search /> },
          { id: "orgs", label: "Organisations", href: "#/orgs", icon: <Building2 /> },
        ],
      },
      {
        id: "safety",
        label: "Safety",
        items: [
          {
            id: "screening",
            label: "Screening",
            href: "#/screening",
            icon: <ShieldAlert />,
            ...(queue > 0 ? { badge: String(queue) } : {}),
          },
          { id: "denylist", label: "Denylist", href: "#/denylist", icon: <SlashSquare /> },
        ],
      },
      {
        id: "record",
        items: [
          { id: "audit", label: "Audit trail", href: "#/audit", icon: <ScrollText /> },
          // Reads are auditor-level and the screen renders read-only for
          // anybody else, but the rail only offers it to administrators: they
          // are the only accounts that can change one, and an item nobody else
          // can act on is noise in the incident that matters.
          ...(canAdminister
            ? [{ id: "flags", label: "Flags", href: "#/flags", icon: <ToggleLeft /> }]
            : []),
        ],
      },
    ],
    [queue, canAdminister],
  );
}

/** Who you are, what you may do, and the way out. */
function Identity({ me }: { me: Me }): React.JSX.Element {
  const queryClient = useQueryClient();
  const signOut = useMutation({
    mutationFn: () => api.signOut(),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      window.location.hash = "";
      window.location.reload();
    },
  });

  const role = me.can.administer ? "Admin" : me.can.operate ? "Operator" : "Auditor";

  return (
    <>
      <RoleBadge role={role} permissions={me.permissions} />
      <Mono className="lg:hidden lg:text-caption lg:text-muted-foreground lg:md:inline">
        {me.email}
      </Mono>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          signOut.mutate();
        }}
      >
        Sign out
      </Button>
    </>
  );
}

function SignIn({
  forbidden,
  message,
  branding,
}: {
  forbidden: boolean;
  message: string;
  branding: Branding;
}): React.JSX.Element {
  const start = useCallback(() => {
    const returnTo = window.location.hash || "#/";
    window.location.href = `/auth/login?returnTo=${encodeURIComponent(`/${returnTo}`)}`;
  }, []);

  return (
    <SplashLayout
      brand={<LagoonLockup product={branding.productName} />}
      title={forbidden ? "Not this surface" : "Operations"}
      description={
        forbidden ? message : "Sign in with your Black Pearl account to reach the console."
      }
      footer={
        <span>
          Trouble? <Mono>{branding.supportEmail}</Mono>
        </span>
      }
    >
      {forbidden ? (
        <Text size="sm" tone="muted">
          This surface is restricted to the Tech Ops groups. Ask an administrator to add you.
        </Text>
      ) : (
        <Button block onClick={start}>
          <LifeBuoy />
          Sign in
        </Button>
      )}
    </SplashLayout>
  );
}

/**
 * The product name, support address and operator, as the admin service
 * templated them onto <body>.
 *
 * The fallback is the shared default rather than a literal typed here, so the
 * bundle holds no second copy of the name to go stale. It applies only to the
 * Vite dev server, which serves `index.html` without going through the service.
 */
function readBranding(): Branding {
  return {
    productName: readConfig("productName", DEFAULT_BRANDING.productName),
    supportEmail: readConfig("supportEmail", DEFAULT_BRANDING.supportEmail),
    legalEntity: readConfig("legalEntity", DEFAULT_BRANDING.legalEntity),
  };
}

/** Read a value the admin service injected as a data attribute on <body>. */
function readConfig(key: string, fallback: string): string {
  const value = document.body.dataset[key];
  return value === undefined || value === "" ? fallback : value;
}
